import React, {useCallback, useState} from "react";
import {Hotline, HotlineCall, HotlineNumber,} from "../typedefs";
import {Action, ActionSelect} from "../../../components/ui/ActionSelect";
import {useNavigateWithClientID} from "../hooks/useNavigateWithClientID";
import moment from "moment";
import ModalV2 from "../../../components/portal/ModalV2";
import {PaginatedSelector} from "./PaginatedSelector";
import {fetchHotlineCalls} from "../hooks/useGetHotlineCalls";
import {ButtonV2} from "../../../components/ui/ButtonsV2/ButtonV2";
import {apiV2} from "../../../api/axiosConfig";
import {fetchHotlineNumbers} from "../hooks/useGetHotlineNumbers";
import {formatPhoneNumber} from "../../../lib/pnone/formatPhoneNumber";
import {usePermissions} from "../../../context/modulePermissions";

type HotlineRowProps = {
    hotline: Hotline;
    refetch: () => void;
}

export function HotlineRow({hotline, refetch}: HotlineRowProps) {

    const {navigateWithClientID} = useNavigateWithClientID()

    const navigateToEdit = useCallback(() => {
        navigateWithClientID(`siren/hotlines/${hotline.id}/edit`)
    }, [navigateWithClientID, hotline.id])


    const [showCalls, setShowCalls] = useState<boolean>(false)
    const {hasFullAccess} = usePermissions()

    const getCalls = useCallback((search: string, page: number) => {
        return fetchHotlineCalls(page, 10, search, hotline.id)
    }, [hotline, fetchHotlineCalls])

    const [disabling, setDisabling] = useState<boolean>(false)

    const getCallColumns = useCallback((item: HotlineCall) => {
        return [
            item.callerName,
            item.callerE164,
            moment(item.startTime).format("DD/MM/YYYY HH:mm:ss"),
            item.completed ? "Yes" : "No"
        ]
    }, [])


    const [showNumbers, setShowNumbers] = useState(false)

    const getNumbers = useCallback((search: string, page: number) => {
        return fetchHotlineNumbers(page, 10, search, hotline.id)
    }, [hotline, fetchHotlineCalls])


    const getNumberColumns = useCallback((item: HotlineNumber) => {
        return [
            item.name,
            formatPhoneNumber(item.phoneNumber.e164)
        ]
    }, [])


    const [showDisable, setShowDisable] = useState<boolean>(false)

    const disable = useCallback(async () => {
        setDisabling(true)
        try {
            await apiV2.post(`hotlines/${hotline.id}/disable`)
            refetch()
        } catch {

        }
        setDisabling(false)
        setShowDisable(false)

    }, [hotline.id, refetch])



    return (
        <>


            <ModalV2 open={showCalls} setOpen={setShowCalls}>
                <h2 className="text-lg font-bold">{hotline.name} Calls</h2>

                <PaginatedSelector
                    headers={["Name", "Phone Number", "Date/Time", "Completed"]}
                    getItems={getCalls}
                    getColumns={getCallColumns}
                    disableSelect={true}
                />
            </ModalV2>

            <ModalV2 open={showNumbers} setOpen={setShowNumbers}>
                <h2 className="text-lg font-bold">{hotline.name} Phone Numbers</h2>

                <PaginatedSelector
                    headers={["Name", "Phone Number"]}
                    getItems={getNumbers}
                    getColumns={getNumberColumns}
                    disableSelect={true}

                />
            </ModalV2>

            <ModalV2 open={showDisable} setOpen={setShowDisable}>
                <h2 className="text-lg font-bold">Disable {hotline.name}</h2>
                <p>Are you sure you want to disable this hotline?</p>
                <ButtonV2 label="Disable" onClick={disable} color="orange" loading={disabling}/>
            </ModalV2>

            <div className={"w-full"}>
                <div className="grid grid-cols-5 gap-8 w-full h-10">
                    <div className="truncate overflow-ellipsis items-center flex">
                        <a href="javascript:void(0)" onClick={navigateToEdit} className="flex items-center gap-4">
                            {hotline.name}
                        </a>
                    </div>
                    <div>{moment(hotline.startTime).format("DD/MM/YYYY HH:mm")}</div>
                    <div>{moment(hotline.endTime).format("DD/MM/YYYY HH:mm")}</div>
                    <div>{hotline.disabled ? "No" : "Yes"}</div>
                    <div>
                        <ActionSelect label={"Actions"}>

                            <Action label="Show Calls"
                                    onSelect={() => setShowCalls(true)}/>


                            <Action label="Show Phone Numbers"
                                    onSelect={() => setShowNumbers(true)}/>

                            <Action label="Disable"
                                    onSelect={() => setShowDisable(true)}
                                    hide={hotline.disabled || !hasFullAccess("siren_resources")}

                            />

                        </ActionSelect>

                    </div>
                </div>
            </div>
        </>

    )
}